import { CommonModule } from '@angular/common';
import {
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router, RouterLink } from '@angular/router';
import { Subject, filter, take } from 'rxjs';
import { fuseAnimations } from 'src/@fuse/animations';
import { FuseConfigService } from 'src/@fuse/services/config.service';
import { SharedModule } from 'src/app/shared/shared.module';

import { LoadingComponent } from '../../../generals/loading/loading.component';
import { PinCodeComponent } from '../../../generals/pin-code/pin-code.component';
import { WellcomeComponent } from '../../../generals/wellcome/wellcome.component';
import { MicroResponse } from '../../../services/microservices/response/micro.response';
import { UserResponse } from '../../../services/microservices/response/user.response';
import { ProspectService } from '../../../services/prospect.service';
import { UserService } from '../../../services/user.service';
import { SimpleProspectInfo } from '../object-type/SimpleProspectInfo';

@Component({
  selector: 'app-recover-registry',
  templateUrl: './recover-registry.component.html',
  styleUrls: ['./recover-registry.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    LoadingComponent,
    MatButtonModule,
    MatCheckboxModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatSnackBarModule,
    MatTooltipModule,
    PinCodeComponent,
    ReactiveFormsModule,
    RouterLink,
    SharedModule,
    WellcomeComponent,
  ],
  encapsulation: ViewEncapsulation.None,
  animations: fuseAnimations,
})
export class RecoverRegistryComponent implements OnInit, OnDestroy {
  email = '';
  user: any;
  prospectData!: SimpleProspectInfo;
  recoverForm: FormGroup = this.getRecoverForm();
  emailRecoverForm: FormGroup = this.getEmailRecoverForm();
  @ViewChild(PinCodeComponent) pinCodeComp!: PinCodeComponent;
  isValidate: boolean;
  isRegisterConfirm: boolean;
  isShowChangeEmail: boolean;
  isEmailRegistered = false;
  isLoading = false;

  private _unsubscribeAll: Subject<any>;

  constructor(
    private _fuseConfigService: FuseConfigService,
    private _formBuilder: FormBuilder,
    private _prospectService: ProspectService,
    private _userService: UserService,
    private _snackBar: MatSnackBar,
    private _router: Router,
  ) {
    // Configure the layout
    this._fuseConfigService.config = {
      layout: {
        navbar: { hidden: true },
        toolbar: { hidden: true },
        footer: { hidden: true },
        sidepanel: { hidden: true },
      },
    };
    this.isValidate = true;
    this.isRegisterConfirm = false;
    this.isShowChangeEmail = false;
    this._unsubscribeAll = new Subject();
  }

  ngOnInit(): void {
    const item = sessionStorage.getItem('prospectData');
    if (item) {
      sessionStorage.removeItem('prospectData');
      this.prospectData = JSON.parse(item);
    }
  }

  processPin(pinCode: string): void {
    if (pinCode) {
      this.recoverForm.patchValue({ pinCode });
      this.recoverForm.patchValue({ verifedPinCode: pinCode.length === 6 });
    }
  }

  showConfirmRegister(): void {
    this.isLoading = true;

    if (!this.prospectData) {
      this.isLoading = false;
      this._snackBar.open('No se encontraron datos del prospecto', '', {
        verticalPosition: 'top',
        horizontalPosition: 'right',
        panelClass: ['snack-error'],
      });
      return;
    }

    this._prospectService
      .checkPinCode(
        this.prospectData.id,
        this.recoverForm.get('pinCode')?.value,
      )
      .subscribe({
        next: response => {
          if (response.data && this.prospectData) {
            this.email = this.prospectData.email;
            this.sendEmail();
            return;
          }
          this.isLoading = false;
          this.isValidate = true;
          this.isRegisterConfirm = false;
          this.pinCodeComp.resetComponent();
          this.recoverForm.patchValue({ verifedPinCode: false });
          this._snackBar.open('Pin incorrecto', '', {
            verticalPosition: 'top',
            horizontalPosition: 'right',
            panelClass: ['snack-error'],
          });
        },
        error: () => {
          this.isLoading = false;
          this.isValidate = true;
          this.isRegisterConfirm = false;
          this.recoverForm.patchValue({ verifedPinCode: false });
          this._snackBar.open('Pin incorrecto', '', {
            verticalPosition: 'top',
            horizontalPosition: 'right',
            panelClass: ['snack-error'],
          });
        },
      });
  }

  sendEmail(): void {
    if (!this.prospectData) {
      this.isLoading = false;
      this._snackBar.open('No se encontraron datos del prospecto', '', {
        verticalPosition: 'top',
        horizontalPosition: 'right',
        panelClass: ['snack-error'],
      });
      return;
    }

    this._prospectService.sendEmail(this.prospectData.id).subscribe({
      next: () => {
        this.isValidate = false;
        this.isRegisterConfirm = true;
        this.isLoading = false;
        this.checkEmailRegistered();
      },
      error: () => {
        this.isLoading = false;
      },
    });
  }

  private checkEmailRegistered() {
    const intervalo = setInterval(() => {
      if (this.isEmailRegistered) {
        clearInterval(intervalo);
      } else {
        const userEmail = this._userService.getUserByEmail(this.email).pipe(
          filter(response => !(response === null)),
          take(1),
        );
        userEmail.subscribe({
          next: (response: MicroResponse<UserResponse>) => {
            this.isEmailRegistered = this.email === response.data.email;
            this.isShowChangeEmail = false;
          },
        });
      }
    }, 7000);
  }

  checkName(data: string): void {
    const input = this.recoverForm.get(data);
    if (!input || !this.prospectData) return;

    const strData = (input.value as keyof string).toString().toUpperCase();
    if (strData !== this.prospectData[data as keyof SimpleProspectInfo]) {
      input.setErrors({ nameNotMatch: true });
    }
  }

  viewChangeEmail(): void {
    this.isShowChangeEmail = true;
    this.isLoading = false;
  }

  onSubmitChangeEmail(): void {
    this.isLoading = true;
    const control = this.emailRecoverForm.get('email') as any;
    const valor = control.value.toLowerCase();
    if (control.valid) {
      this._userService.checkInfo(valor).subscribe({
        next: (emailResponse: MicroResponse<UserResponse>) => {
          if (emailResponse) {
            control.setErrors({ emailExiste: true });
            this.isLoading = false;
          } else {
            if (control.hasError('emailExiste')) {
              delete control.errors.emailExiste;
            }
            this.saveEmail();
          }
        },
      });
    }
  }

  private saveEmail() {
    this.email = this.emailRecoverForm.get('email')?.value;

    if (!this.prospectData) {
      this.isLoading = false;
      this._snackBar.open('No se encontraron datos del prospecto', '', {
        verticalPosition: 'top',
        horizontalPosition: 'right',
        panelClass: ['snack-error'],
      });
      return;
    }

    this._prospectService
      .saveEmail(this.prospectData.id, { email: this.email })
      .subscribe({
        next: (response: MicroResponse<boolean>) => {
          if (response?.data) {
            this.sendEmail();
            this.isShowChangeEmail = false;
            this.emailRecoverForm.get('email')?.reset();
            return;
          }
          this.isLoading = false;
          const control = this.emailRecoverForm['controls']['email'];
          control.setErrors({ emailExiste: true });
        },
        error: () => {
          this.isLoading = false;
          this._snackBar.open(
            'Hubo un error con el envío del correo en la dirección especificada, inténtalo de nuevo',
            undefined,
            { horizontalPosition: 'right', panelClass: ['snack-error'] },
          );
        },
      });
  }

  exit(event: any) {
    event.preventDefault();
    this._router.navigate(['/auth/login']);
  }

  getRecoverForm(): FormGroup {
    return this._formBuilder.group({
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      pinCode: ['', [Validators.required, Validators.minLength(6)]],
      cardId: ['', [Validators.minLength(10), Validators.maxLength(10)]],
      verifedPinCode: [false, Validators.requiredTrue],
    });
  }

  getEmailRecoverForm(): FormGroup {
    return this._formBuilder.group({
      email: ['', [Validators.required, Validators.email]],
    });
  }

  hasNombreError(): boolean | undefined {
    const nombreControl = this.recoverForm.get('firstName');
    return (
      nombreControl?.invalid && (nombreControl?.touched || nombreControl?.dirty)
    );
  }

  hasApellidoError(): boolean | undefined {
    const apellidoControl = this.recoverForm.get('lastName');
    return (
      apellidoControl?.invalid &&
      (apellidoControl?.touched || apellidoControl?.dirty)
    );
  }

  hasEmailError(): boolean | undefined {
    const emailControl = this.emailRecoverForm.get('email');
    return (
      emailControl?.invalid && (emailControl?.touched || emailControl?.dirty)
    );
  }

  getNombreErrorTooltip(): string {
    const nombreControl = this.recoverForm.get('firstName');

    if (nombreControl?.hasError('required')) {
      return 'Nombre es requerido';
    } else if (nombreControl?.hasError('nombreNoCoincide')) {
      return 'Nombre no coincide con la cedula ingresada';
    }

    return '';
  }

  getApellidoErrorTooltip(): string {
    const apellidoControl = this.recoverForm.get('lastName');

    if (apellidoControl?.hasError('required')) {
      return 'Apellido es requerido';
    } else if (apellidoControl?.hasError('nombreNoCoincide')) {
      return 'Apellido no coincide con la cedula ingresada';
    }

    return '';
  }

  getEmailErrorTooltip(): string {
    const emailControl = this.emailRecoverForm.get('email');

    if (emailControl?.hasError('required')) {
      return 'Email es requerido';
    } else if (emailControl?.hasError('email')) {
      return 'Email no valido';
    } else if (emailControl?.hasError('emailExiste')) {
      return 'Este Email ya es usuario de idempier';
    }

    return '';
  }

  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next(void 0);
    this._unsubscribeAll.complete();
  }
}
